/**
 * errorToast.ts — the React analog of AngularJS's global $http error interceptor
 * (app/scripts/providers/httpinterceptor.js). Wired into the QueryClient's query/mutation
 * caches (services/queryClient.ts) so every failed data call surfaces an error toast by
 * default, matching the legacy behavior. Callers that present their own error UI opt out
 * via `meta: { suppressErrorToast: true }`.
 *
 * Faithful to the legacy interceptor, this toasts on:
 *   - status 0  → server unreachable
 *   - status 400 with a structured `errors[]` body → one toast per server message
 * and stays silent otherwise (401 is the auth/token-refresh flow; 5xx was never toasted).
 */
import { ApiError } from './api';
import { notifyError } from './notify';

const GET_HELP_BUTTON =
  '<a href="javascript:window.showChat()" class="btn btn-primary"><i class="icon-question-sign"></i> Get Help</a>';

export function showApiErrorToast(error: unknown): void {
  // Only classify errors we threw ourselves; anything else stays silent (matches legacy).
  if (!(error instanceof ApiError)) return;

  // The auth flow handles 401 (token refresh / login redirect) — never toast it.
  if (error.status === 401) return;

  if (error.status === 0) {
    notifyError({
      title: 'Server Error',
      description:
        'Could not communicate with the server. Please try again in a few minutes. If the problem persists please contact support.',
    });
    return;
  }

  const messages = error.data?.errors
    ?.map((e) => e.defaultMessage)
    .filter((m): m is string => !!m);

  if (messages?.length) {
    messages.forEach((description) =>
      notifyError({ title: 'An Error Occurred', description, buttons: GET_HELP_BUTTON }),
    );
  }
  // No structured message (e.g. 5xx) — stay silent, as the legacy interceptor did.
}
